interface Service {
  title: string
  description: string
  features: string[]
  benefits: string[]
}

interface Services {
  [key: string]: Service
}

interface ServicesTranslations {
  title: string
  subtitle: string
  professionalService: string
  whatsIncluded: string
  services: Services
  cta: {
    text: string
    button: string
  }
}

export const servicesTranslations: Record<'en' | 'cy', ServicesTranslations> = {
  en: {
    title: "Professional Garden Care Services",
    subtitle: "Comprehensive garden maintenance and care to keep your outdoor space beautiful and thriving",
    professionalService: "Professional garden care service",
    whatsIncluded: "What's Included",
    services: {
      maintenance: {
        title: "Regular Garden Maintenance",
        description: "Keep your garden looking its best with regular maintenance services. From mowing and weeding to hedge trimming and general tidying.",
        features: [
          "Lawn Mowing & Edging",
          "Weed Control & Removal",
          "Hedge Trimming & Shaping",
          "Leaf Collection & Removal",
          "Garden Tidying & Clean-up",
          "Path & Driveway Sweeping",
          "Fence & Gate Repair",
          "Plant Health Monitoring",
          "General Garden Care"
        ],
        benefits: [
          "Regular maintenance keeps your garden looking neat and tidy",
          "Professional care helps plants stay healthy and strong",
          "Consistent service ensures your garden always looks its best"
        ]
      },
      seasonal: {
        title: "Seasonal Garden Care",
        description: "Adapt your garden care to the changing seasons with specialized services that help your garden thrive year-round.",
        features: [
          "Spring Garden Preparation",
          "Summer Maintenance & Watering",
          "Autumn Clean-up & Leaf Removal",
          "Winter Protection & Pruning",
          "Seasonal Planting & Bedding",
          "Fertilizing & Mulching",
          "Pest & Disease Control",
          "Garden Planning & Advice"
        ],
        benefits: [
          "Seasonal care ensures your garden adapts to weather changes",
          "Proper timing maximizes plant health and growth",
          "Year-round beauty with appropriate seasonal maintenance"
        ]
      },
      planting: {
        title: "Planting & Bedding",
        description: "Add color and life to your garden with professional planting services, from seasonal bedding to perennial borders.",
        features: [
          "Seasonal Bedding Plants",
          "Perennial Planting",
          "Shrub & Tree Planting",
          "Container Planting",
          "Hanging Basket Creation",
          "Bulb Planting",
          "Seed Sowing",
          "Plant Selection Advice"
        ],
        benefits: [
          "Professional planting ensures proper establishment",
          "Expert advice on plant selection and placement",
          "Beautiful displays that last and thrive"
        ]
      },
      tidying: {
        title: "Garden Tidying & Clean-up",
        description: "Transform overgrown and neglected gardens into clean, manageable spaces ready for new life and enjoyment.",
        features: [
          "Overgrowth Removal",
          "Bramble & Weed Clearance",
          "Dead Plant Removal",
          "Garden Waste Disposal",
          "Soil Preparation",
          "Path & Border Clearing",
          "Pressure Washing for Patios & Driveways",
          "Fence & Gate Installation",
          "Tool & Equipment Cleaning",
          "Garden Assessment & Planning"
        ],
        benefits: [
          "Clean slate for new garden projects",
          "Safe and thorough removal of unwanted growth",
          "Professional disposal of garden waste"
        ]
      },
      treeSurgery: {
        title: "Tree Surgery",
        description: "Professional tree surgery services to manage the health, safety, and aesthetics of your trees, from pruning to removal.",
        features: [
          "Tree Pruning & Crown Reduction",
          "Tree Felling & Removal",
          "Stump Grinding & Removal",
          "Tree Health Assessments",
          "Hedge Trimming & Reduction",
          "Emergency Tree Work",
          "Branch Removal & Deadwooding",
          "Site Clearance"
        ],
        benefits: [
          "Improves tree health and structure",
          "Removes dangerous or unwanted trees safely",
          "Enhances property safety and aesthetics"
        ]
      },
      fencing: {
        title: "Fencing Solutions",
        description: "Enhance your property's privacy and security with my custom fencing installation and design services.",
        features: [
          "Custom Fence Design & Installation",
          "Privacy & Security Fencing",
          "Decorative & Ornamental Fencing",
          "Wide Range of Materials (Wood, Metal, Vinyl)",
          "Fence Staining & Painting",
          "Eco-Friendly Fencing Options",
          "Post & Rail Fencing",
          "Gate Installation"
        ],
        benefits: [
          "Increases property value and curb appeal",
          "Provides privacy and a secure boundary",
          "Durable, high-quality materials for longevity"
        ]
      }
    },
    cta: {
      text: "Ready to give your garden the care it deserves?",
      button: "Get Your Free Quote"
    }
  },
  cy: {
    title: "Gwasanaethau Gofal Gardd Proffesiynol",
    subtitle: "Cynnal a chadw gardd cynhwysfawr i gadw eich lle awyr agored yn hardd a ffynnu",
    professionalService: "Gwasanaeth gofal gardd proffesiynol",
    whatsIncluded: "Beth Sy'n Gynnwys",
    services: {
      maintenance: {
        title: "Cynnal a Chadw Gardd Rheolaidd",
        description: "Cadwch eich gardd yn edrych yn ei gorau gyda gwasanaethau cynnal a chadw rheolaidd. O dorri gwair a chwynnu i trimio gwrychoedd a thacluso cyffredinol.",
        features: [
          "Torri Gwair a Finio",
          "Rheoli a Dileu Chwyn",
          "Trimio a Siapio Gwrychoedd",
          "Casglu a Dileu Dail",
          "Tacluso a Glanhau Gardd",
          "Ysgubo Llwybrau a Drwyfeydd",
          "Atgyweirio Ffensys a Gatiau",
          "Monitro Iechyd Planhigion",
          "Gofal Gardd Cyffredinol"
        ],
        benefits: [
          "Mae cynnal a chadw rheolaidd yn cadw eich gardd yn edrych yn daclus a threfnus",
          "Mae gofal proffesiynol yn helpu planhigion i aros yn iach a chryf",
          "Mae gwasanaeth cyson yn sicrhau bod eich gardd bob amser yn edrych yn ei gorau"
        ]
      },
      seasonal: {
        title: "Gofal Gardd Tymhorol",
        description: "Addaswch eich gofal gardd i'r tymhorau sy'n newid gyda gwasanaethau arbenigol sy'n helpu eich gardd i ffynnu trwy gydol y flwyddyn.",
        features: [
          "Paratoi Gardd y Gwanwyn",
          "Cynnal a Chadw a Dyfrio Haf",
          "Glanhau Hydref a Dileu Dail",
          "Amddiffyn a Thocio Gaeaf",
          "Plannu a Gwelyau Tymhorol",
          "Gwrtaethu a Mulch",
          "Rheoli Pla a Chlefydau",
          "Cynllunio a Chyngor Gardd"
        ],
        benefits: [
          "Mae gofal tymhorol yn sicrhau bod eich gardd yn addasu i newidiadau tywydd",
          "Mae amseru priodol yn mwyhau iechyd a thwf planhigion",
          "Harddwch trwy gydol y flwyddyn gyda chynnal a chadw tymhorol priodol"
        ]
      },
      planting: {
        title: "Plannu a Gwelyau",
        description: "Ychwanegwch liw a bywyd i'ch gardd gyda gwasanaethau plannu proffesiynol, o blanhigion gwelyau tymhorol i ffiniau lluosflwydd.",
        features: [
          "Planhigion Gwelyau Tymhorol",
          "Plannu Lluosflwydd",
          "Plannu Llwyni a Choed",
          "Plannu Cynwysyddion",
          "Creu Basgedi Hanging",
          "Plannu Bylbiau",
          "Hau Hadau",
          "Cyngor Dewis Planhigion"
        ],
        benefits: [
          "Mae plannu proffesiynol yn sicrhau sefydlu priodol",
          "Cyngor arbenigol ar ddewis a lleoli planhigion",
          "Arddangosiadau hardd sy'n para ac yn ffynnu"
        ]
      },
      tidying: {
        title: "Tacluso a Glanhau Gardd",
        description: "Trawsnewidiwch gerddoedd wedi'u gor-dyfu a heb eu gofalu yn leoedd glân, y gellir eu rheoli yn barod ar gyfer bywyd newydd a mwynhad.",
        features: [
          "Dileu Gor-dyfiant",
          "Clirio Mieri a Chwyn",
          "Dileu Planhigion Marw",
          "Gwaredu Gwastraff Gardd",
          "Paratoi Pridd",
          "Clirio Llwybrau a Ffiniau",
          "Golchi Pwysedd ar gyfer Patios a Drwyfeydd",
          "Gosod Ffensys a Gatiau",
          "Glanhau Offer ac Offer",
          "Asesiad a Chynllunio Gardd"
        ],
        benefits: [
          "Slate glân ar gyfer prosiectau gardd newydd",
          "Dileu diogel a thrylwyr o dyfiant dymunol",
          "Gwaredu proffesiynol o wastraff gardd"
        ]
      },
      treeSurgery: {
        title: "Llawfeddygaeth Coed",
        description: "Gwasanaethau llawfeddygaeth coed proffesiynol i reoli iechyd, diogelwch ac estheteg eich coed, o docio i dynnu.",
        features: [
          "Tocio Coed a Gostwng y Goron",
          "Cwympo a Symud Coed",
          "Malurio a Symud Bonion",
          "Asesiadau Iechyd Coed",
          "Trimio a Gostwng Gwrychoedd",
          "Gwaith Coed Brys",
          "Symud Canghennau a Phren Marw",
          "Clirio Safle"
        ],
        benefits: [
          "Yn gwella iechyd a strwythur coed",
          "Yn symud coed peryglus neu ddiangen yn ddiogel",
          "Yn gwella diogelwch ac estheteg eiddo"
        ]
      },
      fencing: {
        title: "Datrysiadau Ffensio",
        description: "Gwella preifatrwydd a diogelwch eich eiddo gyda fy ngwasanaethau gosod a dylunio ffensys pwrpasol.",
        features: [
          "Dylunio a Gosod Ffensys Pwrpasol",
          "Ffensys Preifatrwydd a Diogelwch",
          "Ffensys Addurniadol ac Addurnol",
          "Amrywiaeth Eang o Ddeunyddiau (Pren, Metel, Vinyl)",
          "Stenio a Pheintio Ffensys",
          "Opsiynau Ffensio Eco-gyfeillgar",
          "Ffensio Post a Rheilffordd",
          "Gosod Gatiau"
        ],
        benefits: [
          "Yn cynyddu gwerth eiddo ac apêl y stryd",
          "Yn darparu preifatrwydd a ffin ddiogel",
          "Deunyddiau gwydn, o ansawdd uchel am hirhoedledd"
        ]
      }
    },
    cta: {
      text: "Yn barod i roi'r gofal y mae eich gardd yn ei haeddu?",
      button: "Cael Eich Dyfynbris Am Ddim"
    }
  }
} 